// atoms
import { selectedGroupingScheduleAtom } from '../../atoms';

// components
import { Button, Menu, MenuItem } from '@mui/material';

// enums
import { View, ViewContext } from '../../enums';

// hooks
import { useEffect, useMemo, useState } from 'react';
import { useNavParams, useSelector } from 'hooks';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';

// types
import type { IconType } from '@sdb/planning-ui/ui-3/packages/icons/__generated__';

// utils
import { getViewContext, resolveAvailableView } from '../../utils';
import { Icon } from '@sdb/planning-ui';

type Props = {
  view: View;
  handleChangeView: (view: View) => void;
  disabled?: boolean;
};

const viewIcon: { [T in View]: IconType } = {
  [View.Roster]: 'grid-2',
  [View.RosterPragmatic]: 'grid-2',
  [View.Timeline]: 'timeline-arrow',
  [View.TimelinePragmatic]: 'timeline-arrow',
  [View.List]: 'bars',
  [View.Cards]: 'grid-list',
  [View.CardsPragmatic]: 'grid-list',
  [View.Map]: 'map-location-dot'
};

export default function ViewSwitch({
  view,
  handleChangeView,
  disabled
}: Props) {
  const { t } = useTranslation('common');
  const {
    hasActivityPlanningViewAccess,
    hasWorkPlacePlanningViewAccess,
    hasMyActivitiesProjectAccess,
    hasManageOwnProjectsAccess,
    hasKidsPlanAccess,
    hasActivityPlanningDndKitAccess,
    hasActivityPlanningPragmaticAccess,
    hasActivityPlanningRosterDndKitAccess,
    hasActivityPlanningRosterPragmaticAccess,
    hasActivityPlanningTimelineDndKitAccess,
    hasActivityPlanningTimelinePragmaticAccess
  } = useSelector(({ grants }) => grants);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [, setSelectedGroupingSchedule] = useRecoilState(
    selectedGroupingScheduleAtom
  );
  const { params, setParams } = useNavParams();
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);

  // `getViewContext` reads `window.location.hash` (external mutable state);
  // capture it per render and include it in the memo deps so a context change
  // can't leave a stale `views` list.
  const viewContext = getViewContext();

  const views = useMemo<View[]>(() => {
    let result: View[] = [];
    if (
      hasActivityPlanningViewAccess ||
      hasWorkPlacePlanningViewAccess ||
      hasMyActivitiesProjectAccess ||
      hasManageOwnProjectsAccess ||
      hasKidsPlanAccess
    ) {
      if (hasActivityPlanningRosterDndKitAccess) {
        result.push(View.Roster);
      }
      if (hasActivityPlanningRosterPragmaticAccess) {
        result.push(View.RosterPragmatic);
      }
    }
    if (
      hasActivityPlanningViewAccess ||
      hasWorkPlacePlanningViewAccess ||
      hasMyActivitiesProjectAccess ||
      hasManageOwnProjectsAccess
    ) {
      result.push(View.List);
    }
    if (hasActivityPlanningViewAccess) {
      if (hasActivityPlanningTimelineDndKitAccess) {
        result.push(View.Timeline);
      }
      if (hasActivityPlanningTimelinePragmaticAccess) {
        result.push(View.TimelinePragmatic);
      }
      if (hasActivityPlanningDndKitAccess) {
        result.push(View.Cards);
      }
      if (hasActivityPlanningPragmaticAccess) {
        result.push(View.CardsPragmatic);
      }
      result.push(View.Map);
    }

    // Only Roster and List view when viewing My Activities
    if (viewContext === ViewContext.Own) {
      result = result.filter(
        (v: View) =>
          v === View.List || v === View.Roster || v === View.RosterPragmatic
      );
    }

    return result;
  }, [
    viewContext,
    hasActivityPlanningViewAccess,
    hasWorkPlacePlanningViewAccess,
    hasMyActivitiesProjectAccess,
    hasManageOwnProjectsAccess,
    hasKidsPlanAccess,
    hasActivityPlanningDndKitAccess,
    hasActivityPlanningPragmaticAccess,
    hasActivityPlanningRosterDndKitAccess,
    hasActivityPlanningRosterPragmaticAccess,
    hasActivityPlanningTimelineDndKitAccess,
    hasActivityPlanningTimelinePragmaticAccess
  ]);

  useEffect(() => {
    const resolved = resolveAvailableView(view, views);
    if (resolved && resolved !== view) {
      setParams({ ...params, view: resolved });
      handleChangeView(resolved);
    }
  }, [view, views, params, setParams, handleChangeView]);

  return (
    <div>
      <Button
        variant="outlined"
        disabled={disabled}
        sx={{
          display: 'flex',
          gap: (theme) => theme.spacing(2)
        }}
        onClick={handleClick}
        startIcon={<Icon type={viewIcon[view]} />}
        endIcon={<Icon type="chevron-down" fontSize="small" />}
      >
        {t(`activityPlanning.view.${view}`)}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        {views.map((v) => (
          <MenuItem
            key={v}
            color="neutral"
            onClick={() => {
              setParams({ ...params, view: v });
              handleChangeView(v);
              if (v !== View.Map) {
                setSelectedGroupingSchedule('');
              }
              setAnchorEl(null);
            }}
          >
            <Icon type={viewIcon[v]} sx={{ mr: 1.5 }} fontSize="small" />
            {t(`activityPlanning.view.${v}`)}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
}
